import { useEffect, useState } from "react";

/**
 * What language the editor's settings panel speaks.
 *
 * Every control in the panel already knew how to say itself in English — each one read
 * `from_eng=1` out of the URL on its own and picked a label set. That made the language a
 * property of the lecture rather than of the reader: someone working an English source got an
 * English panel with no way back, and someone working a Russian source could not get an English
 * one at all, however little Russian they read.
 *
 * So the choice moves here, one answer for the whole panel:
 *   • the reader's explicit pick, if they made one — it is remembered across sessions;
 *   • otherwise the lecture's own direction, `from_eng=1` meaning the source is English and so,
 *     by the convention this editor already used, an English-speaking operator.
 *
 * An explicit pick outranks the URL on purpose: the toggle would be a lie if opening the next
 * lecture silently undid it.
 */

export type UiLang = "ru" | "en";

const STORAGE_KEY = "editorUiLanguage";

const listeners = new Set<(lang: UiLang) => void>();

/** Only used where localStorage throws (private modes, embedded views). */
let memoryFallback: UiLang | null = null;

const readStored = (): UiLang | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw === "ru" || raw === "en") return raw;
        return memoryFallback;
    } catch {
        return memoryFallback;
    }
};

/** The lecture's own direction: translating FROM English implies an English-reading operator. */
const fromUrl = (): UiLang => {
    if (typeof window === "undefined") return "ru";
    return new URLSearchParams(window.location.search).get("from_eng") === "1" ? "en" : "ru";
};

/**
 * Read every time rather than caching the choice in a module variable: the stored value is the
 * single answer, and a copy of it in memory is one more thing that can disagree with it.
 */
export const getUiLang = (): UiLang => readStored() ?? fromUrl();

export const setUiLang = (lang: UiLang): void => {
    try {
        localStorage.setItem(STORAGE_KEY, lang);
    } catch {
        // A browser that refuses storage still gets the switch for this session.
        memoryFallback = lang;
    }
    listeners.forEach((fn) => fn(lang));
};


export const useUiLang = (): UiLang => {
    const [lang, setLang] = useState<UiLang>(getUiLang);
    useEffect(() => {
        const update = () => setLang(getUiLang());
        listeners.add(update);
        update();
        return () => {
            listeners.delete(update);
        };
    }, []);
    return lang;
};

/** Convenience for the many controls written as `fromEng ? "..." : "..."`. */
export const useIsEnglishUi = (): boolean => useUiLang() === "en";

/**
 * The names of the source language, in the panel's language.
 *
 * The category above the source controls cannot be headed «Русский оригинал» when the lecture is
 * translated FROM English: there is no Russian in it. Russian needs the adjective in three
 * shapes, so all three are given rather than glued together at the call site.
 */
export const sourceLanguageNames = (
    code: string | undefined,
    ui: UiLang,
): { nom: string; acc: string; adjPl: string } => {
    const lang = String(code || "ru").toLowerCase();
    if (ui === "en") {
        const name = lang === "ru" ? "Russian" : lang === "en" ? "English" : lang.toUpperCase();
        return { nom: name, acc: name, adjPl: name };
    }
    if (lang === "en") return { nom: "Английский", acc: "английский", adjPl: "английские" };
    if (lang === "ru") return { nom: "Русский", acc: "русский", adjPl: "русские" };
    const upper = lang.toUpperCase();
    return { nom: upper, acc: upper, adjPl: upper };
};
